/**
 * 🎥 VIDEO FLOATING TOOLBAR 2025
 * 
 * Floating Toolbar für Video Elements
 * - YouTube & Vimeo URL Input
 * - Size Controls (Width, Height, Aspect Ratio)
 * - Styling (Border Radius, Shadow)
 * - Margins (Desktop/Mobile)
 * - Alignment
 * 
 * ✅ Matched to ButtonFloatingToolbar Design:
 * - Drag & Drop functionality
 * - Icon-based sections
 * - Pill-shaped → rounded transition
 * - Desktop/Mobile toggles in sections
 * - No title header
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './VideoFloatingToolbar.css';

function VideoFloatingToolbar({
    isEditMode,
    isVisible,
    
    // Video URL
    videoUrl = '',
    
    // Size
    videoWidth = 100,
    videoWidthMobile,
    videoWidthType = '%', // '%' oder 'px'
    videoHeight = 500,
    videoHeightMobile = 400,
    aspectRatio = '16-9', // '16-9' | '4-3' | 'square' | 'custom'
    
    // Alignment
    videoAlignment = 'center',
    
    // Border Radius
    borderRadius = 12,
    borderRadiusMobile,
    
    // Border
    borderEnabled = false,
    borderWidth = 2,
    borderColor = '#667eea',
    
    // Shadow
    shadowEnabled = false,
    shadowBlur = 16,
    shadowDistance = 8,
    shadowOpacity = 25,
    
    // Margins (Desktop)
    marginTop = 20,
    marginBottom = 20,
    marginLeft = 0,
    marginRight = 0,
    
    // Margins (Mobile)
    marginTopMobile,
    marginBottomMobile,
    marginLeftMobile,
    marginRightMobile,
    
    // Callbacks
    onStyleChange,
    onClose,
    colors = {},
    isMobileLayout = false
}) {
    const [showToolbar, setShowToolbar] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    const toolbarRef = useRef(null);
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [toolbarPosition, setToolbarPosition] = useState({ top: 80, left: window.innerWidth / 2 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 400);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 300);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // Sync device with isMobileLayout
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // Show/Hide Toolbar
    useEffect(() => {
        if (isVisible) {
            setShowToolbar(true);
        } else {
            setExpandedSection(null);
            setTimeout(() => setShowToolbar(false), 200);
        }
    }, [isVisible]);
    
    // Outside Click Handler
    useEffect(() => {
        if (!showToolbar) return;
        
        const handleClickOutside = (event) => {
            if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
                setExpandedSection(null);
                if (onClose) onClose();
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showToolbar, onClose]);
    
    if (!isEditMode || !showToolbar) return null;
    
    // Active Values (responsive)
    const activeWidth = device === 'mobile' 
        ? (videoWidthMobile ?? videoWidth)
        : videoWidth;
    
    const activeHeight = device === 'mobile'
        ? (videoHeightMobile ?? videoHeight)
        : videoHeight;
    
    const activeBorderRadius = device === 'mobile'
        ? (borderRadiusMobile ?? borderRadius)
        : borderRadius;
    
    const activeMarginTop = device === 'mobile' ? (marginTopMobile ?? marginTop) : marginTop;
    const activeMarginBottom = device === 'mobile' ? (marginBottomMobile ?? marginBottom) : marginBottom;
    const activeMarginLeft = device === 'mobile' ? (marginLeftMobile ?? marginLeft) : marginLeft;
    const activeMarginRight = device === 'mobile' ? (marginRightMobile ?? marginRight) : marginRight;
    
    // Update Handlers
    const handleUpdate = (updates) => {
        if (onStyleChange) {
            onStyleChange(updates);
        }
    };
    
    const handleMarginChange = (side, value) => {
        const propName = device === 'mobile'
            ? `margin${side}Mobile`
            : `margin${side}`;
        handleUpdate({ [propName]: parseInt(value) });
    };
    
    const handleBorderRadiusChange = (value) => {
        const propName = device === 'mobile' ? 'borderRadiusMobile' : 'borderRadius';
        handleUpdate({ [propName]: parseInt(value) });
    };
    
    const handleWidthChange = (value) => {
        const propName = device === 'mobile' ? 'videoWidthMobile' : 'videoWidth';
        handleUpdate({ [propName]: parseInt(value) });
    };
    
    const handleHeightChange = (value) => {
        const propName = device === 'mobile' ? 'videoHeightMobile' : 'videoHeight';
        handleUpdate({ [propName]: parseInt(value) });
    };
    
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`video-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="video-toolbar"
                onMouseDown={(e) => {
                    // ✅ Stoppe mousedown Events innerhalb der Toolbar
                    e.stopPropagation();
                }}
            >
                {/* 🎨 MAIN TOOLBAR BUTTONS - Icon Row */}
                <div className="video-toolbar-row">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="video-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* 1. Video URL */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'url' ? null : 'url');
                        }}
                        className={`video-toolbar-btn ${expandedSection === 'url' ? 'is-active' : ''}`}
                        title="Video URL (YouTube/Vimeo)"
                    >
                        <i className="fas fa-link"></i>
                    </button>
                    
                    {/* 2. Size & Aspect Ratio */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'size' ? null : 'size');
                        }}
                        className={`video-toolbar-btn ${expandedSection === 'size' ? 'is-active' : ''}`}
                        title="Size & Aspect Ratio"
                    >
                        <i className="fas fa-expand"></i>
                    </button>
                    
                    {/* 3. Styling */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'style' ? null : 'style');
                        }}
                        className={`video-toolbar-btn ${expandedSection === 'style' ? 'is-active' : ''} ${(borderEnabled || shadowEnabled) ? 'has-feature' : ''}`}
                        title="Styling (Border, Shadow)"
                    >
                        <i className="fas fa-paint-brush"></i>
                    </button>
                    
                    {/* 4. Spacing */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'margins' ? null : 'margins');
                        }}
                        className={`video-toolbar-btn ${expandedSection === 'margins' ? 'is-active' : ''}`}
                        title="Spacing (Margins)"
                    >
                        <i className="fas fa-arrows-alt-v"></i>
                    </button>
                    
                    {/* 5. Alignment */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'alignment' ? null : 'alignment');
                        }}
                        className={`video-toolbar-btn ${expandedSection === 'alignment' ? 'is-active' : ''}`}
                        title="Alignment"
                    >
                        <i className="fas fa-align-center"></i>
                    </button>
                </div>
                
                {/* Expanded Sections */}
                
                {/* 1️⃣ URL SECTION */}
                {expandedSection === 'url' && (
                    <div className="video-expanded-section">
                        <div className="video-url-input-group">
                            <i className="fas fa-link"></i>
                            <input
                                type="text"
                                className="video-url-input"
                                placeholder="Paste YouTube or Vimeo URL..."
                                value={videoUrl}
                                onChange={(e) => handleUpdate({ videoUrl: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                        
                        <div className="video-url-hint">
                            <i className="fas fa-info-circle"></i>
                            <span>Supports: youtube.com/watch?v=... or youtu.be/... or vimeo.com/...</span>
                        </div>
                        
                        <span className="video-section-label">Video URL</span>
                    </div>
                )}
                
                {/* 2️⃣ SIZE SECTION */}
                {expandedSection === 'size' && (
                    <div className="video-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Aspect Ratio */}
                        <div className="video-control-group">
                            <label className="video-control-label">Aspect Ratio</label>
                            <div className="video-aspect-buttons">
                                <button
                                    className={`video-aspect-btn ${aspectRatio === '16-9' ? 'active' : ''}`}
                                    onClick={() => handleUpdate({ aspectRatio: '16-9' })}
                                    title="16:9 (YouTube Standard)"
                                >
                                    16:9
                                </button>
                                <button
                                    className={`video-aspect-btn ${aspectRatio === '4-3' ? 'active' : ''}`}
                                    onClick={() => handleUpdate({ aspectRatio: '4-3' })}
                                    title="4:3"
                                >
                                    4:3
                                </button>
                                <button
                                    className={`video-aspect-btn ${aspectRatio === 'square' ? 'active' : ''}`}
                                    onClick={() => handleUpdate({ aspectRatio: 'square' })}
                                    title="1:1 (Square)"
                                >
                                    1:1
                                </button>
                                <button
                                    className={`video-aspect-btn ${aspectRatio === 'custom' ? 'active' : ''}`}
                                    onClick={() => handleUpdate({ aspectRatio: 'custom' })}
                                    title="Custom Height"
                                >
                                    Custom
                                </button>
                            </div>
                        </div>
                        
                        {/* Width */}
                        <div className="typo-control">
                            <label>
                                <i className="fas fa-arrows-alt-h"></i>
                                Width
                            </label>
                            <input
                                type="range"
                                min="20"
                                max={videoWidthType === '%' ? '100' : '2000'}
                                step={videoWidthType === '%' ? '5' : '10'}
                                value={activeWidth}
                                onChange={(e) => handleWidthChange(e.target.value)}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeWidth}{videoWidthType}</span>
                            <div className="video-unit-toggle">
                                <button
                                    className={`video-unit-btn ${videoWidthType === '%' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleUpdate({ videoWidthType: '%' });
                                    }}
                                >
                                    %
                                </button>
                                <button
                                    className={`video-unit-btn ${videoWidthType === 'px' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleUpdate({ videoWidthType: 'px' });
                                    }}
                                >
                                    px
                                </button>
                            </div>
                        </div>
                        
                        {/* Height (nur bei Custom Aspect Ratio) */}
                        {aspectRatio === 'custom' && (
                            <div className="typo-control">
                                <label>
                                    <i className="fas fa-arrows-alt-v"></i>
                                    Height
                                </label>
                                <input
                                    type="range"
                                    min="200"
                                    max="1000"
                                    step="10"
                                    value={activeHeight}
                                    onChange={(e) => handleHeightChange(e.target.value)}
                                    className="typo-slider"
                                />
                                <span className="typo-val">{activeHeight}px</span>
                            </div>
                        )}
                        
                        <span className="video-section-label">Size & Dimensions</span>
                    </div>
                )}
                
                {/* 3️⃣ STYLE SECTION */}
                {expandedSection === 'style' && (
                    <div className="video-expanded-section video-effects-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="typo-control">
                            <label>
                                <i className="fas fa-circle"></i>
                                Radius
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                step="2"
                                value={activeBorderRadius}
                                onChange={(e) => handleBorderRadiusChange(e.target.value)}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeBorderRadius}px</span>
                        </div>
                        
                        {/* 🔲 BORDER */}
                        <div className="video-effect-group">
                            <div className="video-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={borderEnabled}
                                        onChange={(e) => handleUpdate({ borderEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Border</span>
                                </label>
                            </div>
                            
                            {borderEnabled && (
                                <div className="video-effect-controls">
                                    {/* Border Width */}
                                    <div className="typo-control">
                                        <label>Width</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="10"
                                            step="1"
                                            value={borderWidth}
                                            onChange={(e) => handleUpdate({ borderWidth: parseInt(e.target.value) })}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{borderWidth}px</span>
                                    </div>
                                    
                                    {/* Border Color - Brand Colors */}
                                    <div className="video-brand-colors video-brand-colors-small">
                                        {Object.values(colors).slice(0, 5).map((color, index) => (
                                            <button
                                                key={index}
                                                className="video-brand-color video-brand-color-small"
                                                style={{ backgroundColor: color }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleUpdate({ borderColor: color });
                                                }}
                                                title={`Border Color ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="video-effect-divider" />
                        
                        {/* 🌟 SHADOW */}
                        <div className="video-effect-group">
                            <div className="video-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={shadowEnabled}
                                        onChange={(e) => handleUpdate({ shadowEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Shadow</span>
                                </label>
                            </div>
                            
                            {shadowEnabled && (
                                <div className="video-effect-controls">
                                    {/* Shadow Blur */}
                                    <div className="typo-control">
                                        <label>Blur</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            step="2"
                                            value={shadowBlur}
                                            onChange={(e) => handleUpdate({ shadowBlur: parseInt(e.target.value) })}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{shadowBlur}px</span>
                                    </div>
                                    
                                    {/* Shadow Distance */}
                                    <div className="typo-control">
                                        <label>Distance</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            step="1"
                                            value={shadowDistance}
                                            onChange={(e) => handleUpdate({ shadowDistance: parseInt(e.target.value) })}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{shadowDistance}px</span>
                                    </div>
                                    
                                    {/* Shadow Opacity */}
                                    <div className="typo-control">
                                        <label>Opacity</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="80"
                                            step="5"
                                            value={shadowOpacity}
                                            onChange={(e) => handleUpdate({ shadowOpacity: parseInt(e.target.value) })}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{shadowOpacity}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="video-section-label">Styling</span>
                    </div>
                )}
                
                {/* 4️⃣ MARGINS SECTION */}
                {expandedSection === 'margins' && (
                    <div className="video-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        <div className="typo-control">
                            <label>↑ Top</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginTop}
                                onChange={(e) => handleMarginChange('Top', e.target.value)}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeMarginTop}px</span>
                        </div>
                        
                        <div className="typo-control">
                            <label>↓ Bottom</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginBottom}
                                onChange={(e) => handleMarginChange('Bottom', e.target.value)}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeMarginBottom}px</span>
                        </div>
                        
                        <div className="typo-control">
                            <label>← Left</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginLeft}
                                onChange={(e) => handleMarginChange('Left', e.target.value)}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeMarginLeft}px</span>
                        </div>
                        
                        <div className="typo-control">
                            <label>→ Right</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginRight}
                                onChange={(e) => handleMarginChange('Right', e.target.value)}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeMarginRight}px</span>
                        </div>
                        
                        <span className="video-section-label">Spacing</span>
                    </div>
                )}
                
                {/* 5️⃣ ALIGNMENT SECTION */}
                {expandedSection === 'alignment' && (
                    <div className="video-expanded-section">
                        <div className="video-alignment-buttons">
                            <button
                                className={`video-align-btn ${videoAlignment === 'flex-start' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUpdate({ videoAlignment: 'flex-start' });
                                }}
                                title="Left"
                            >
                                <i className="fas fa-align-left"></i>
                                <span>Left</span>
                            </button>
                            <button
                                className={`video-align-btn ${videoAlignment === 'center' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUpdate({ videoAlignment: 'center' });
                                }}
                                title="Center"
                            >
                                <i className="fas fa-align-center"></i>
                                <span>Center</span>
                            </button>
                            <button
                                className={`video-align-btn ${videoAlignment === 'flex-end' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUpdate({ videoAlignment: 'flex-end' });
                                }}
                                title="Right"
                            >
                                <i className="fas fa-align-right"></i>
                                <span>Right</span>
                            </button>
                        </div>
                        
                        <span className="video-section-label">Alignment</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default VideoFloatingToolbar;

